<template>
  <!-- Wait for permissions before rendering to avoid a 403 flash -->
  <div v-if="!permsLoaded"></div>

  <PageError
    v-else-if="!allowed"
    :error="errorMessages['403']"
    :errorCode="'403'"
  />

  <PageLoader v-else-if="loading" />

  <PageError v-else-if="loadError" :error="loadError" :errorCode="loadErrorCode" />

  <div v-else class="p-6 dark:text-gray-50">
    <div class="flex space-x-3 mb-6 items-center">
      <h1 class="text-2xl font-semibold dark:text-white">
        {{ isEdit ? "Edit Alert Contact" : "Add New Alert Contact" }}
      </h1>
    </div>

    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <form
      @submit.prevent="submitForm"
      @keydown.enter="blockImplicitSubmit"
      class="space-y-6 glass-card p-6"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="flex flex-col">
          <label for="name" class="mb-1 font-medium dark:text-white">
            Name<span class="pl-1 text-red-500">*</span>
          </label>
          <input
            id="name"
            v-model="formData.name"
            type="text"
            @focus="clearFieldError('name')"
            :class="[
              fieldErrors.name
                ? 'rounded px-3 py-2 ring-2 ring-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 mb-2'
                : 'rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2',
            ]"
          />
          <FieldError :message="fieldErrors.name" />
        </div>

        <div class="flex flex-col">
          <label for="msisdn" class="mb-1 font-medium dark:text-white">
            Mobile Number<span class="pl-1 text-red-500">*</span>
          </label>
          <div class="flex flex-col sm:flex-row gap-2 mb-2">
            <div class="sm:w-56 sm:shrink-0">
              <GlassSelect
                v-model="msisdnCountry"
                :options="countryOptions"
                valueKey="countryCode"
                labelKey="label"
                :searchable="true"
                searchPlaceholder="Search country or dialling code..."
                placeholder="Select a country"
                :disabled="submitting || !phoneUtilsReady"
              />
            </div>
            <div class="relative flex-1 mt-1">
              <span
                v-if="dialPrefix"
                class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 dark:text-gray-400"
              >
                {{ dialPrefix }}
              </span>
              <input
                id="msisdn"
                v-model="formData.msisdn"
                type="tel"
                inputmode="tel"
                autocomplete="tel"
                :disabled="submitting"
                :aria-invalid="Boolean(fieldErrors.msisdn)"
                aria-describedby="msisdn-hint msisdn-error"
                @focus="clearFieldError('msisdn')"
                @blur="validateMsisdnField"
                @paste="onMsisdnPaste"
                :style="dialPrefix ? { paddingLeft: `calc(1rem + ${dialPrefix.length}ch)` } : null"
                :class="[
                  fieldErrors.msisdn
                    ? 'w-full rounded px-3 py-2 ring-2 ring-red-500 focus:outline-none focus:ring-2 focus:ring-red-500'
                    : 'w-full rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500',
                ]"
              />
            </div>
          </div>
          <div id="msisdn-error" aria-live="polite">
            <FieldError :message="fieldErrors.msisdn" />
          </div>
          <p
            id="msisdn-hint"
            v-if="!fieldErrors.msisdn"
            class="text-xs text-gray-500 dark:text-gray-400"
          >
            {{ msisdnHintLine }}
          </p>
        </div>
      </div>

      <div class="flex flex-col">
        <AlertGroupPicker v-model="selectedGroupIds" :groups="assignableGroups">
          <template #actions>
            <button
              type="button"
              @click="refreshGroups"
              :disabled="groupsRefreshing || submitting"
              class="rounded px-2.5 py-1 text-sm bg-gray-600 text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ groupsRefreshing ? "Refreshing..." : "Refresh Groups" }}
            </button>
          </template>
        </AlertGroupPicker>
        <FieldError :message="fieldErrors.group_ids" class="mt-2" />
        <p
          v-if="groupsUnavailable"
          class="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400"
        >
          Alert groups could not be loaded.
        </p>
        <p
          v-else-if="selectedGroupIds.length === 0"
          class="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400"
        >
          This contact is not in any alert group and will not receive any alerts until added to one.
        </p>
      </div>

      <div class="flex justify-end space-x-2">
        <button type="button" @click="goBack" class="btn-cancel">Cancel</button>
        <button
          type="submit"
          :disabled="submitting"
          class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ submitting ? "Saving..." : isEdit ? "Save Changes" : "Add Alert Contact" }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import {
  createAlertContact,
  updateAlertContact,
  getAlertContactById,
  getAllAlertGroups,
  refreshAlertGroups,
} from "@/services/alertContactService";
import FieldError from "@/components/FieldError.vue";
import PageError from "@/components/PageError.vue";
import AlertGroupPicker from "@/components/AlertGroupPicker.vue";
import PageLoader from "@/components/PageLoader.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useStore } from "@/store";
import { errorMessages, extractErrorMessage, formatError } from "@/utils/errorMessages";
import {
  MSISDN_DEFAULT_REGION,
  canValidateMsisdn,
  dialCodeForCountry,
  isValidMsisdn,
  listMsisdnCountries,
  loadPhoneNumberUtils,
  msisdnHintForCountry,
  msisdnRejectionReason,
  normalizeMsisdn,
  splitInternationalMsisdn,
} from "@/utils/phoneNumber";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "AlertContactForm",
  components: {
    FieldError,
    PageError,
    AlertGroupPicker,
    PageLoader,
    GlassSelect,
  },
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
  },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      error: "",
      errorCode: "",
      errorMessages,
      fieldErrors: {},
      formData: {
        name: "",
        msisdn: "",
      },
      groups: [],
      contactGroups: [],
      selectedGroupIds: [],
      loading: false,
      loadError: "",
      loadErrorCode: "",
      submitting: false,
      pickedCountry: MSISDN_DEFAULT_REGION,
      groupsRefreshing: false,
      phoneUtilsReady: false,
      groupsUnavailable: false,
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    isEdit() {
      return this.id !== null && this.id !== undefined && this.id !== "";
    },
    // Create needs add-perm; edit needs change-perm. Superuser bypasses both.
    allowed() {
      if (this.user?.is_superuser) return true;
      return this.isEdit
        ? useStore.hasPermission("Can change alert contact")
        : useStore.hasPermission("Can add alert contact");
    },
    // orphaned assigned groups are hidden here; the backend preserves those
    // memberships on update regardless of the submitted group_ids
    assignableGroups() {
      return this.groups;
    },
    countryOptions() {
      return this.phoneUtilsReady ? listMsisdnCountries() : [];
    },
    msisdnCountry: {
      get() {
        return this.pickedCountry;
      },
      set(countryCode) {
        this.pickedCountry = countryCode;
        this.clearFieldError("msisdn");
      },
    },
    dialPrefix() {
      return this.phoneUtilsReady ? dialCodeForCountry(this.pickedCountry) : "";
    },
    msisdnHintLine() {
      return msisdnHintForCountry(this.pickedCountry);
    },
  },
  watch: {
    // Refetch when navigating between edit targets without unmounting.
    id() {
      this.init();
    },
    permsLoaded: {
      handler(loaded) {
        if (loaded && this.allowed) this.init();
      },
      immediate: true,
    },
    selectedGroupIds() {
      this.clearFieldError("group_ids");
    },
  },
  methods: {
    async init() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadPhoneUtils(),
          this.fetchGroups(),
          this.tenant.fetchOrganizations().catch(() => null),
        ]);
        if (this.isEdit) await this.fetchContact();
      } finally {
        this.loading = false;
      }
    },
    async loadPhoneUtils() {
      await loadPhoneNumberUtils();
      this.phoneUtilsReady = canValidateMsisdn();
    },
    async refreshGroups() {
      const validation = this.tenant.validateSchemaForOperation();
      if (!validation.valid) return;
      this.groupsRefreshing = true;
      try {
        await refreshAlertGroups(validation.client);
        setTimeout(async () => {
          await this.fetchGroups();
          this.groupsRefreshing = false;
        }, 4000);
      } catch (error) {
        this.groupsRefreshing = false;
        this.error =
          error?.response?.status === 429
            ? "Groups were refreshed recently. Please wait a minute and try again."
            : "Failed to refresh alert groups.";
      }
    },
    async fetchGroups() {
      try {
        // URL-derived client, not currentSchemaName: on a cold reload the org
        // isn't pinned yet, so currentSchemaName is null and the endpoint 422s.
        const validation = this.tenant.validateSchemaForOperation();
        const groups = await getAllAlertGroups(validation.client);
        this.groups = groups || [];
        this.groupsUnavailable = false;
      } catch (error) {
        console.error("Error fetching alert groups:", error);
        this.groups = [];
        this.groupsUnavailable = true;
      }
    },
    async fetchContact() {
      try {
        this.loadError = "";
        const validation = this.tenant.validateSchemaForOperation();
        if (!validation.valid) {
          this.loadError = "Malformed request. Failed to load alert contact!";
          this.loadErrorCode = "422!";
          return;
        }
        const contact = await getAlertContactById(this.id, validation.client);
        const split = splitInternationalMsisdn(`+${contact.msisdn}`);
        if (split) this.pickedCountry = split.countryCode;
        this.formData = {
          name: contact.name || "",
          msisdn: split ? split.nationalNumber : contact.msisdn || "",
        };
        this.contactGroups = contact.groups || [];
        this.selectedGroupIds = this.contactGroups.map((group) => group.id);
      } catch (error) {
        console.error("Error loading alert contact:", error);
        const { code, message } = formatError(error);
        this.loadErrorCode = code;
        this.loadError = message;
      }
    },
    applyInternationalSplit() {
      if (!this.phoneUtilsReady) return;
      const split = splitInternationalMsisdn(this.formData.msisdn);
      if (!split) return;
      this.pickedCountry = split.countryCode;
      this.formData.msisdn = split.nationalNumber;
    },
    onMsisdnPaste() {
      this.$nextTick(this.applyInternationalSplit);
    },
    validateMsisdnField() {
      this.applyInternationalSplit();
      if (!this.phoneUtilsReady || !this.formData.msisdn) return;
      if (isValidMsisdn(this.formData.msisdn, this.pickedCountry)) {
        delete this.fieldErrors.msisdn;
        return;
      }
      this.fieldErrors.msisdn = msisdnRejectionReason(this.formData.msisdn, this.pickedCountry);
    },
    blockImplicitSubmit(event) {
      if (event.target.tagName !== "BUTTON") event.preventDefault();
    },
    clearFieldError(fieldName) {
      this.error = "";
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
      }
    },
    goBack() {
      this.$router.push({
        name: "AlertContacts",
        query: this.tenant.addSchemaParam(),
      });
    },
    validateForm() {
      this.fieldErrors = {};
      if (!this.formData.name || this.formData.name.trim() === "") {
        this.fieldErrors.name = "This field is required.";
      }
      if (!this.formData.msisdn || this.formData.msisdn.trim() === "") {
        this.fieldErrors.msisdn = "This field is required.";
      } else if (this.phoneUtilsReady && !isValidMsisdn(this.formData.msisdn, this.pickedCountry)) {
        this.fieldErrors.msisdn = msisdnRejectionReason(this.formData.msisdn, this.pickedCountry);
      }
      return Object.keys(this.fieldErrors).length === 0;
    },
    async submitForm() {
      this.error = "";
      const validation = this.tenant.validateSchemaForOperation();
      if (!validation.valid) {
        this.error = `Malformed request. Failed to ${this.isEdit ? "update" : "create"} alert contact!`;
        this.errorCode = "422!";
        return;
      }
      if (!this.validateForm()) return;

      const payload = {
        name: this.formData.name.trim(),
        msisdn: normalizeMsisdn(this.formData.msisdn, this.pickedCountry) || this.formData.msisdn.trim(),
        group_ids: this.selectedGroupIds,
      };

      this.submitting = true;
      try {
        if (this.isEdit) {
          await updateAlertContact(this.id, payload, validation.client);
        } else {
          await createAlertContact(payload, validation.client);
        }
        this.goBack();
      } catch (error) {
        this.mapSubmitError(error);
        console.error(`Error ${this.isEdit ? "updating" : "creating"} alert contact:`, error);
      } finally {
        this.submitting = false;
      }
    },
    mapSubmitError(error) {
      this.fieldErrors = {};
      const response = error?.response;
      const errData = response?.data;
      const isValidationError =
        response?.status === 400 &&
        errData && typeof errData === "object" && !Array.isArray(errData);
      if (isValidationError) {
        const fieldKeys = new Set(["name", "msisdn", "group_ids"]);
        const generalMessages = [];
        Object.entries(errData).forEach(([key, value]) => {
          const raw = Array.isArray(value) ? value[0] : value;
          if (!raw) return;
          const message = typeof raw === "string" ? raw : JSON.stringify(raw);
          if (fieldKeys.has(key)) {
            this.fieldErrors[key] = message;
          } else {
            generalMessages.push(message);
          }
        });
        if (generalMessages.length) {
          this.errorCode = "400";
          this.error = generalMessages.join(" ");
        }
      } else if (response) {
        this.errorCode = response.status ? String(response.status) : "";
        this.error = extractErrorMessage(
          error,
          ` Something went wrong while ${this.isEdit ? "saving" : "creating"} the alert contact. Please try again.`
        );
      } else {
        this.errorCode = "";
        this.error = "Network error or server unreachable. Please try again.";
      }
    },
  },
};
</script>
